import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TikTokMetadataPopout from "./TikTokMetadataPopout";
import "./gameArtCards.css";

const INITIAL_RENDER_COUNT = 80;
const LOAD_MORE_COUNT = 80;

const TikTokEyeIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M2.8 12s3.45-5.7 9.2-5.7 9.2 5.7 9.2 5.7-3.45 5.7-9.2 5.7S2.8 12 2.8 12Z"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.05" stroke={color} strokeWidth="1.9" />
  </svg>
);

const TikTokCameraIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="4.25"
      y="7.25"
      width="10.5"
      height="9.5"
      rx="2"
      stroke={color}
      strokeWidth="1.9"
    />
    <path
      d="M14.75 10.25 20 7.85v8.3l-5.25-2.4v-3.5Z"
      stroke={color}
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
  </svg>
);

const TikTokCardSkeleton = () => (
  <div style={{ width: "100%", margin: 0 }}>
    <div
      className="mg-game-art-skeleton"
      style={{
        backgroundImage:
          "linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%)",
        backgroundSize: "200% 100%",
        animation: "tt-shimmer 1.4s linear infinite",
      }}
    />
  </div>
);

const renderItem = (
  game,
  i,
  setShowInsights,
  comparisonGames,
  setComparisonGames,
  onToggleComparisonGame,
  onRemoveGame,
  openMetadataKey,
  setOpenMetadataKey,
  metadataSide,
  setMetadataSide
) => {
  const selectionKey = game.selectionKey || game.name;
  const groupSelectionKeys = game.groupSelectionKeys?.length
    ? game.groupSelectionKeys
    : [selectionKey];
  const selected = groupSelectionKeys.some(key => comparisonGames.includes(key)) ||
    comparisonGames.includes(game.name);
  const latestVideos = game.rankedMetrics && game.rankingMetric === "views";
  const displayVideos = latestVideos ? Number(game.videos) :
    game.rankedMetrics ? game.videosTotal : Number(game.videosTotal ?? game.videos ?? 0);
  const displayViews = game.rankedMetrics ? game.viewsTotal : Number(game.viewsTotal ?? game.views ?? 0);
  const showCardLabels = i === 0;
  const hashtag = String(game.hashtag || game.name).trim().replace(/^#+/, "");
  const hashtags = (Array.isArray(game.groupHashtags) && game.groupHashtags.length
    ? game.groupHashtags
    : [hashtag])
    .map(value => String(value || "").trim().replace(/^#+/, ""))
    .filter(Boolean);
  const hashtagUrl = value => `https://www.tiktok.com/tag/${encodeURIComponent(value)}`;

  return (
    <div key={selectionKey || i} style={{ position: "relative", width: "100%", minWidth: 0, margin: 0 }}
      onMouseEnter={event => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMetadataSide(rect.right + 350 <= window.innerWidth
          ? "right" : rect.left >= 350 ? "left" : "below");
        setOpenMetadataKey(selectionKey);
      }}
      onMouseLeave={() => setOpenMetadataKey(null)}
      onFocusCapture={event => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMetadataSide(rect.right + 350 <= window.innerWidth
          ? "right" : rect.left >= 350 ? "left" : "below");
        setOpenMetadataKey(selectionKey);
      }}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpenMetadataKey(null);
        }
      }}>
      <div data-card className="mg-game-art-card">
        <a className="mg-game-art-media" href={hashtagUrl(hashtag)} target="_blank" rel="noopener noreferrer">
          <img
            src={game.imageUrl}
            alt={game.name}
            loading={i < 20 ? "eager" : "lazy"}
            decoding="async"
            fetchpriority={i < 20 ? "high" : "low"}
          />

          <div className="mg-game-art-rank" style={{ position: "absolute", top: 0, left: 0, width: "auto", whiteSpace: "nowrap", color: "#fff" }}>
            #{i + 1}
          </div>
        </a>
        <button className="mg-game-art-remove" aria-label={`Remove ${game.displayName || game.name}`}
          onClick={() => onRemoveGame(selectionKey)}
          style={{ position: "absolute", top: 0, right: 0, zIndex: 13, color: "#fff", border: "none", padding: 0, cursor: "pointer" }}>
          ×
        </button>

        <div className="mg-game-art-footer" style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 22px",
          background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.74) 48%, rgba(0,0,0,0) 100%)",
          pointerEvents: "auto",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, color: "#cfd8e3", fontSize: 12, lineHeight: "18px", textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>
            {hashtags.map(tag => (
              <a key={tag.toLowerCase()} href={hashtagUrl(tag)} target="_blank" rel="noopener noreferrer"
                style={{ display: "block", color: "#9fb7c9", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180, textDecoration: "none" }}>
                #{tag}
              </a>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 19, minWidth: 19, height: 19, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#9fb7c9", transform: "translateY(0.5px)" }}>
                <TikTokCameraIcon />
              </span>
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4, minWidth: 0 }}>
                <strong style={{ color: "#fff", fontWeight: 800 }}>{displayVideos == null ? "—" : displayVideos.toLocaleString()}</strong>
                {showCardLabels &&
                  <span style={{ whiteSpace: "nowrap" }}>{latestVideos ? "Videos" : `Videos ${game.metricLabel || ""}`.trim()}</span>}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 19, minWidth: 19, height: 19, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#9fb7c9", transform: "translateY(0.5px)" }}>
                <TikTokEyeIcon />
              </span>
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4, minWidth: 0 }}>
                <strong style={{ color: "#fff", fontWeight: 800 }}>{displayViews == null ? "—" : displayViews.toLocaleString()}</strong>
                {showCardLabels && <span style={{ whiteSpace: "nowrap" }}>Views {game.metricLabel}</span>}
              </span>
            </div>
            {game.coverageLabel && <span title={game.coverageLabel} style={{ fontSize: 10, color: "#f5e6c8" }}>{game.coverageLabel}</span>}
          </div>

          <button
            className="mg-game-art-add"
            onClick={(e) => {
              e.preventDefault();
              if (onToggleComparisonGame) {
                onToggleComparisonGame(game);
                return;
              }
              setShowInsights(true);
              setComparisonGames(previous => {
                const isSelected = groupSelectionKeys.some(key => previous.includes(key)) ||
                  previous.includes(game.name);
                return isSelected
                  ? previous.filter(key => !groupSelectionKeys.includes(key) && key !== game.name)
                  : [...new Set([...previous, ...groupSelectionKeys])];
              });
            }}
            style={{
              width: 22,
              minWidth: 22,
              height: 22,
              flex: "0 0 22px",
              justifySelf: "end",
              position: "absolute",
              right: 0,
              bottom: 0,
              border: "none",
              color: "#fff",
              fontSize: 15,
              lineHeight: "15px",
              fontWeight: 900,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
            }}
            aria-label={selected ? `Remove ${game.displayName || game.name} from comparison` : `Add ${game.displayName || game.name} to comparison`}
          >
            {selected ? "✓" : "+"}
          </button>
        </div>
      </div>
      {openMetadataKey === selectionKey && (
        <TikTokMetadataPopout
          game={game}
          side={metadataSide}
          onMouseEnter={() => setOpenMetadataKey(selectionKey)}
        />
      )}
    </div>
  );
};

export default function VirtualizedTikTokGrid({
  games,
  loading = false,
  setShowInsights,
  comparisonGames,
  setComparisonGames,
  onToggleComparisonGame,
  onRemoveGame,
}) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_RENDER_COUNT);
  const [openMetadataKey, setOpenMetadataKey] = useState(null);
  const [metadataSide, setMetadataSide] = useState("right");
  const sentinelRef = useRef(null);
  const gridRef = useRef(null);

  useLayoutEffect(() => {
    const dashboard = gridRef.current?.closest(".mg-jura-dashboard");
    const rankingHeader = dashboard?.querySelector(".mg-tiktok-ranking-header");
    if (!rankingHeader) return undefined;
    const appHeader = document.querySelector(".mg-app-header");

    const updateOffset = () => {
      const appHeaderHeight = appHeader?.getBoundingClientRect().height ?? 70;
      rankingHeader.style.setProperty("--tt-selector-sticky-top", `${appHeaderHeight}px`);
    };
    updateOffset();
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateOffset)
      : null;
    if (observer) {
      observer.observe(rankingHeader);
      if (appHeader) observer.observe(appHeader);
    }
    else window.addEventListener("resize", updateOffset);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateOffset);
      rankingHeader.style.removeProperty("--tt-selector-sticky-top");
    };
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_RENDER_COUNT);
  }, [games.length, games[0]?.name, games[games.length - 1]?.name]);

  const visibleGames = useMemo(
    () => games.slice(0, visibleCount),
    [games, visibleCount]
  );

  useEffect(() => {
    if (loading) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, games.length));
        }
      },
      { root: null, rootMargin: "900px 0px", threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, games.length]);

  return (
    <>
      <style>{`
        @keyframes tt-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

      `}</style>

      <div
        ref={gridRef}
        className="mg-game-art-grid"
      >
        {loading
          ? Array.from({ length: 30 }).map((_, idx) => (
              <TikTokCardSkeleton key={idx} />
            ))
          : visibleGames.map((game, idx) =>
              renderItem(
                game,
                idx,
                setShowInsights,
                comparisonGames,
                setComparisonGames,
                onToggleComparisonGame,
                onRemoveGame,
                openMetadataKey,
                setOpenMetadataKey,
                metadataSide,
                setMetadataSide
              )
            )}
      </div>

      {!loading && visibleCount < games.length && (
        <div ref={sentinelRef} style={{ height: 90 }} />
      )}
    </>
  );
}